import React, { useState, useEffect, useRef } from "react";
import "./LoadingProgress.scss";
import { useTranslation } from "../utils/translations/TranslationProvider";
import { SupportedLanguage } from "../utils/translations/types";
import { useRTL } from "../utils/translations";
import TermsModal from "../TermsModal";
import SpecDetails from "../SpecDetails/SpecDetails";
import { uimetadata, assetUrl, assetSrc } from "@/utils/metadata";
import {
  useSelectedJourney,
  setSelectedJourney,
  getJourneySpecs,
  getJourneys,
  getJourney,
  type JourneyKey,
} from "../utils/selectedJourney";
import {
  useLogEvent,
  AnalyticsIds,
  useAnalyticsSegmentationContext,
} from "../utils/useAnalytics";

// ── useResponsive ────────────────────────────────────────────────────────────
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return { isMobile };
};

// ── Text ─────────────────────────────────────────────────────────────────────
interface TextProps {
  translationKey?: string;
  fallback?: string;
  as?: "span" | "p" | "h1" | "h2" | "h3" | "h4" | "div";
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}
const Text: React.FC<TextProps> = ({
  translationKey,
  fallback,
  as: Component = "span",
  className = "",
  children,
  ...props
}) => {
  const { t } = useTranslation();
  if (children)
    return (
      <Component className={className} {...props}>
        {children}
      </Component>
    );
  if (translationKey)
    return (
      <Component className={className} {...props}>
        {t(translationKey, fallback)}
      </Component>
    );
  return <Component className={className} {...props} />;
};

// ── Tooltip ──────────────────────────────────────────────────────────────────
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  width?: number;
  /** "hover" (default) opens on mouse-enter; "click" toggles on click and closes on outside-click. */
  trigger?: "hover" | "click";
}
const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  width = 280,
  trigger = "hover",
}) => {
  const [show, setShow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isClick = trigger === "click";

  // Close the click-popup when the user clicks/taps outside of it.
  useEffect(() => {
    if (!isClick || !show) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isClick, show]);

  const hoverHandlers = isClick
    ? {}
    : {
        onMouseEnter: () => setShow(true),
        onMouseLeave: () => setShow(false),
      };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-flex" }}
      {...hoverHandlers}
      onClick={isClick ? () => setShow((v) => !v) : undefined}
    >
      {children}
      {show && (
        <div
          style={{
            position: "absolute",
            bottom: "130%",
            left: isClick ? 0 : "50%",
            transform: isClick ? "none" : "translateX(-50%)",
            backgroundColor: "rgba(0,0,0,0.9)",
            color: "#fff",
            borderRadius: 'var(--brand-button-radius-sm)',
            padding: "10px 14px",
            fontSize: 13,
            zIndex: 9999,
            width,
            pointerEvents: isClick ? "auto" : "none",
            border: "1px solid rgba(255,255,255,0.1)",
            backdropFilter: "blur(8px)",
          }}
        >
          {content}
        </div>
      )}
    </div>
  );
};

// ── PerformanceStats ─────────────────────────────────────────────────────────
const PerformanceStats: React.FC = () => {
  const { currentLanguage } = useTranslation();
  const { isRTL } = useRTL();
  const journey = useSelectedJourney();
  const performance: any[] = getJourneySpecs(journey)?.performance || [];
  if (!performance.length) return null;
  // `value` is usually a plain string, but may be a {en, ar} map where the
  // unit is part of the word (e.g. "2.0L" / "2.0 لتر").
  const val = (v: any) =>
    v && typeof v === 'object' ? v[currentLanguage] ?? v.en : v;
  return (
    <div
      className={`absolute ${isRTL ? "left-[20%]" : "right-[15%]"} top-[4%] w-auto h-auto z-[2] hidden md:block select-none pointer-events-none`}
    >
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="relative w-full h-full flex flex-col items-end gap-6 text-white text-right"
      >
        {performance.map((item: any, index: number) => (
          <div
            key={index}
            className="flex items-baseline justify-end gap-3"
            style={{ marginRight: `${20 + index * 15}px` }}
          >
            {isRTL ? (
              <>
                <div className="flex items-baseline gap-2">
                  {item.unit && (
                    <span className="text-[20px] font-normal leading-none uppercase">
                      {item.unit}
                    </span>
                  )}
                  <span className="text-sm font-light text-white/70 tracking-wide">
                    {item.label?.[currentLanguage] || item.label?.en}
                  </span>
                </div>
                <span className="text-[20px] font-normal leading-none">
                  {val(item.value)}
                </span>
              </>
            ) : (
              <>
                <span className="text-[20px] font-normal leading-none">
                  {val(item.value)}
                </span>
                <div className="flex items-baseline gap-2">
                  {item.unit && (
                    <span className="text-[20px] font-normal leading-none uppercase">
                      {item.unit}
                    </span>
                  )}
                  <span className="text-sm font-light text-white/70 tracking-wide">
                    {item.label?.[currentLanguage] || item.label?.en}
                  </span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── CarInfoDisplay ────────────────────────────────────────────────────────────
interface CarInfoDisplayProps {
  modelId: string;
  onOpenSpecDetails?: () => void;
  className?: string;
  source?: "loading" | "floating-button";
}
const CarInfoDisplay: React.FC<CarInfoDisplayProps> = ({
  modelId,
  onOpenSpecDetails,
  className = "",
}) => {
  const { currentLanguage, t } = useTranslation();
  const { isRTL } = useRTL();
  const journey = useSelectedJourney();
  const modelMeta = (uimetadata as any).models?.[modelId] || {};
  const modelSpecs = getJourneySpecs(journey) ?? modelMeta.specifications;
  const showLogo = (uimetadata as any).featureFlags?.showLogo ?? false;
  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`z-[2] text-white flex ${isRTL ? "items-start text-right" : "items-start text-left"} justify-between flex-col w-full ${className}`}
    >
      {showLogo && modelMeta.logo && (
        <div className="pb-[1.5rem]">
          <img className="w-[72px] h-[30px]" src={modelMeta.logo} alt="Logo" />
        </div>
      )}
      {modelSpecs?.heading && (
        <div className="mb-2">
          <h2 className="font-normal text-[32px] tracking-wide">
            {modelSpecs.heading?.[currentLanguage] || modelSpecs.heading?.en}
          </h2>
        </div>
      )}
      {onOpenSpecDetails && (
        <button
          onClick={onOpenSpecDetails}
          className="mt-2 flex items-center gap-1.5 text-white/70 hover:text-white text-[16px] font-medium transition-colors bg-transparent border-none p-0 cursor-pointer"
        >
          {t("staticTranslations.loadingScreen.viewSpecDetails", "View Specs")}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              d="M9 18l6-6-6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transform: isRTL ? "scaleX(-1)" : "none",
                transformOrigin: "center",
              }}
            />
          </svg>
        </button>
      )}
    </div>
  );
};

// ── JourneySelector (presentation-mode Stingray / GS X picker) ───────────────
// Renders one card per journey (models.Corvette.journeys). Picking a card updates
// the shared selectedJourney store, which swaps the heading / performance pointers /
// spec-details and filters the configurator's variant list. Renders nothing for
// models that define < 2 journeys.
const JourneySelector: React.FC = () => {
  const { currentLanguage } = useTranslation();
  const { isRTL } = useRTL();
  const active = useSelectedJourney();
  const journeys = getJourneys();
  if (!journeys || journeys.length < 2) return null;
  return (
    <div className="journey-selector-wrapper" dir={isRTL ? "rtl" : "ltr"}>
      <div className="journey-selector-banner">
        <div className="journey-cards">
          {journeys.map((j: any) => {
            const isActive = j.key === active;
            const label = j.name?.[currentLanguage] || j.name?.en || j.key;
            return (
              <button
                key={j.key}
                type="button"
                onClick={() => setSelectedJourney(j.key as JourneyKey)}
                className={`journey-card ${isActive ? "journey-card-active" : ""}`}
                aria-pressed={isActive}
              >
                <div className="journey-card-image">
                  {j.image ? (
                    <img
                      src={assetSrc(j.image)}
                      alt={label}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.visibility =
                          "hidden";
                      }}
                    />
                  ) : null}
                </div>
                <div className="journey-card-name">{label}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Active model from UI metadata config (fallback to Captiva PHEV)
const MODEL: string =
  (uimetadata as unknown as { config?: { model?: string } }).config?.model ||
  "chevrolet-captiva";

interface LoadingProgressProps {
  firstTimeLoad?: boolean;
  // Back-compat alias (caller might use firstTimeLoaded)
  firstTimeLoaded?: boolean;
  /** Optional background video for first-time load */
  videoSrc?: string;
  /** Translation key for the title (e.g., 'common.loading') */
  titleKey?: string;
  /** Direct title text (bypasses translation) */
  title?: string;
  /** Additional CSS classes */
  className?: string;
  /** Animation delay in ms before fade-in starts */
  animationDelay?: number;
  /** Callback when user clicks 'Enter Configurator' button */
  onEnter?: () => void;
  /** Loading duration in ms (default: 3000) */
  loadingDuration?: number;
  /** External loading completion state */
  isLoadingComplete?: boolean;
  /** Callback when user clicks Save inside SpecDetails */
  onSave?: () => void;
  /** Callback when user clicks Share inside SpecDetails */
  onShare?: () => void;
}
const ProgressBar = ({ progress = 0 }: { progress?: number }) => (
  <div className="w-full">
    <div className="h-1 bg-white/20 rounded-full overflow-hidden">
      {progress > 0 ? (
        <div
          className="h-full bg-white rounded-full transition-all duration-300 ease-out shadow-[0_0_8px_rgba(255,255,255,0.5)]"
          style={{ width: `${progress}%` }}
        ></div>
      ) : (
        <div className="h-full bg-white rounded-full indeterminate-progress shadow-[0_0_8px_rgba(255,255,255,0.5)]"></div>
      )}
    </div>
  </div>
);

const SliderButton = ({
  isLoadingComplete,
  handleEnterClick,
}: {
  isLoadingComplete: boolean;
  handleEnterClick: () => void;
}) => {
  const [sliderPosition, setSliderPosition] = useState(5);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isRTL } = useRTL();
  const [colorFactor, setColorFactor] = useState(0);

  const handleStart = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isLoadingComplete) return;
    setDragging(true);
  };

  const handleMove = (e: React.TouchEvent | React.MouseEvent) => {
    if (!dragging || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;

    let newPos;
    if (isRTL) {
      newPos = Math.max(
        5,
        Math.min(containerRect.right - clientX - 20, containerRect.width - 45),
      );
    } else {
      newPos = Math.max(
        5,
        Math.min(clientX - containerRect.left - 20, containerRect.width - 45),
      );
    }
    setSliderPosition(newPos);
    setColorFactor(newPos / containerRect.width);
  };

  const handleEnd = () => {
    if (!dragging || !containerRef.current) return;
    setDragging(false);
    const containerWidth = containerRef.current.offsetWidth;
    if (sliderPosition > containerWidth * 0.7) {
      setSliderPosition(containerWidth - 45);
      setColorFactor(1);
      handleEnterClick();
    } else {
      setSliderPosition(5);
      setColorFactor(0);
    }
  };

  const calculateContainerBackground = () => {
    if (!isLoadingComplete) return "rgba(255, 255, 255, 0.1)";
    if (dragging || sliderPosition > 5) {
      return `rgba(255, 255, 255, ${Math.max(0.1, colorFactor)})`;
    }
    return "rgba(255, 255, 255, 0.1)";
  };

  const isDragged =
    sliderPosition > (containerRef.current?.offsetWidth || 0) / 2;

  return (
    <div
      ref={containerRef}
      className={`slider-button-container ${
        !isLoadingComplete ? "is-loading" : ""
      }`}
      style={{
        background: calculateContainerBackground(),
        transition: dragging ? "none" : "background 0.3s ease-out",
      }}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
    >
      {isLoadingComplete ? (
        <>
          <div
            className="slider-handle"
            style={{
              left: isRTL ? "auto" : `${sliderPosition}px`,
              right: isRTL ? `${sliderPosition}px` : "auto",
              transition: dragging ? "none" : "all 0.3s ease-out",
            }}
            onMouseDown={handleStart}
            onTouchStart={handleStart}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
            >
              <path
                d="M6 6L12 12L6 18"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12 6L18 12L12 18"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className={`slider-text ${isDragged ? "dragged" : ""}`}>
            <Text
              as="span"
              translationKey="staticTranslations.loadingScreen.enterConfigurator"
              fallback="Enter Configurator"
            />
          </div>
        </>
      ) : (
        <div className="loading">
          <Text
            as="span"
            translationKey="staticTranslations.loadingScreen.loading"
            fallback="Loading"
            className="loading-text"
          />
          <div className="loadingDots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadingProgress: React.FC<LoadingProgressProps> = ({
  firstTimeLoad,
  firstTimeLoaded,
  videoSrc: propsVideoSrc,
  titleKey = "loadingScreen.text1",
  title,
  className = "",
  animationDelay = 10,
  onEnter,
  loadingDuration = 3000,
  isLoadingComplete = false,
  onSave,
  onShare,
}) => {
  const metaConfig = (uimetadata as any).config;
  // Presentation hero film and loading slider can differ per journey (Stingray vs
  // GS X). Prefer the active journey's own assets and fall back to
  // `config.presentation.*`, so models that define no per-journey media — or a
  // journey that only overrides some of them — keep working unchanged.
  const activeJourney = useSelectedJourney();
  const journeyMedia = (getJourney(activeJourney) ?? {}) as {
    video?: string;
    videoMobile?: string;
    loadingImage?: string;
  };
  const videoSrc = propsVideoSrc || journeyMedia.video || metaConfig?.presentation?.video;
  const isFirst = (firstTimeLoad ?? firstTimeLoaded ?? true) === true;

  // Check if deferredAssetLoading is enabled
  const deferredAssetLoading = (uimetadata as any).plugins
    ?.deferredAssetLoading;

  // Check if disclaimer should be shown in presentation
  const showDisclaimerInPresentation =
    (uimetadata as any).featureFlags?.showDisclaimerInPresentation ?? true;

  // When deferredAssetLoading is true, button should be enabled immediately
  const buttonEnabled = deferredAssetLoading ? true : isLoadingComplete;
  // Presentation-mode journey picker (Stingray / GS X) — only when the model defines ≥2.
  const hasJourneySelector = getJourneys().length >= 2;

  const [isVisible, setIsVisible] = useState(true);
  const { currentLanguage, setLanguage, t } = useTranslation();
  const { isRTL } = useRTL();
  const { trackEvent } = useLogEvent();
  const { fireModuleStart } = useAnalyticsSegmentationContext();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isSpecDetailsOpen, setIsSpecDetailsOpen] = useState(false);

  useEffect(() => {
    fireModuleStart('landing');
    trackEvent(AnalyticsIds.landingView, { primaryTypeName: 'landing' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isMobile } = useResponsive();
  // Use the phone presentation video on mobile when provided; else the default.
  const resolvedVideoSrc = isMobile
    ? (journeyMedia.videoMobile || metaConfig?.presentation?.videoMobile || videoSrc)
    : videoSrc;
  // React doesn't reliably set the `muted` DOM property before autoplay, so browsers
  // block it. Force muted + call play() on the element to guarantee autoplay.
  const presentationVideoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = presentationVideoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [resolvedVideoSrc]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoadingComplete) {
      setProgress(100);
    } else {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) {
            clearInterval(interval);
            return 95;
          }
          return prev + 1;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isLoadingComplete]);

  const modelData = uimetadata.models?.[MODEL] as unknown as {
    loadingImage?: string;
  };
  const loadingImage =
    journeyMedia.loadingImage ||
    metaConfig?.presentation?.loadingImage ||
    (modelData as { loadingImage?: string })?.loadingImage;

  const languageLabels: Record<SupportedLanguage, string> = {
    en: "English",
    fr: "Français",
    de: "Deutsch",
    es: "Español",
    it: "Italiano",
    ar: "العربية",
  };

  const languages: SupportedLanguage[] = ["en", "ar"];

  const handleLanguageChange = (newLanguage: SupportedLanguage) => {
    trackEvent(AnalyticsIds.languageInteract, {
      parent: 'landing',
      primaryTypeName: newLanguage,
      secondaryTypeName: currentLanguage,
    });
    setLanguage(newLanguage);
    setShowLanguageMenu(false);
  };

  useEffect(() => {
    // Trigger fade-in animation after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, animationDelay);

    return () => clearTimeout(timer);
  }, [animationDelay]);

  const handleEnterClick = () => {
    trackEvent(AnalyticsIds.buildYourRideInteract, {
      parent: 'landing',
    });
    if (onEnter) {
      onEnter();
    }
  };

  if (!isFirst)
    return (
      <div
        style={{
          background: "black",
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "white"
        }}
        className={`loading-progress loading-progress--default ${
          isVisible ? "loading-progress--visible" : ""
        } text-white ${className}`}
      >
        <Text
          as="p"
          translationKey={"staticTranslations.loadingScreen.default"}
          fallback={"loading.title"}
          className="text-2xl text-white mb-8 text-center"
        >
          {title && !titleKey ? title : undefined}
        </Text>
        <div className="w-full max-w-3xl px-6 flex flex-col items-center gap-2">
          <ProgressBar progress={-1} />
        </div>
      </div>
    );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        backgroundColor: "#161616",
        overflow: "hidden"
      }}
      className={`loading-progress-container ${
        isVisible ? "loading-progress--visible" : ""
      } ${className} ${isRTL ? "loading-progress-container--rtl" : ""} ${
        hasJourneySelector ? "has-journey-selector" : ""
      }`}
    >
      <div className={`language ${isRTL ? "language--rtl" : ""}`}>
        <div className="relative">
          <button
            onClick={() => setShowLanguageMenu(!showLanguageMenu)}
            aria-label="Select Language"
            className="flex items-center justify-center bg-transparent border-none p-0"
          >
            <img src={assetUrl + "icons/homepage/translate.svg"} alt="" />
          </button>

          {showLanguageMenu && (
            <div
              className={`absolute top-full mt-2 w-44 bg-black/90 backdrop-blur-xl rounded-lg shadow-2xl border border-white/10 overflow-hidden ${
                isRTL ? "left-0" : "right-0"
              }`}
              style={{ zIndex: 100 }}
            >
              {languages.map((lang) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-white/10 transition-colors duration-150 ${
                    currentLanguage === lang
                      ? "text-blue-400 font-bold"
                      : "text-white/70"
                  }`}
                >
                  {languageLabels[lang]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="videocontainer">
        <video ref={presentationVideoRef} autoPlay muted loop playsInline preload="auto" src={assetUrl + resolvedVideoSrc} key={resolvedVideoSrc} />
        <div className="overlay">
          <CarInfoDisplay
            className={`item-middle-container ${
              isRTL ? "item-middle-container--rtl" : ""
            }`}
            modelId={MODEL}
            onOpenSpecDetails={() => {
              trackEvent(AnalyticsIds.specDetailsInteract, {
                state: true,
                primaryTypeName: 'specDetails',
              });
              setIsSpecDetailsOpen(true);
            }}
            source="loading"
          />

          <div
            className={`ctaGroup ${isRTL ? "ctaGroup--rtl" : ""}`}
            style={{
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {showDisclaimerInPresentation && (
              <Tooltip
                trigger="click"
                content={
                  <div
                    dir={isRTL ? "rtl" : "ltr"}
                    className="text-[12px] leading-snug text-white/90"
                  >
                    {(() => {
                      const note = t(
                        "staticTranslations.loadingScreen.disclaimerNote1",
                        "*On a closed course. Based on initial vehicle movement. With standard Z51 Performance Package",
                      );
                      const hasStar = note.startsWith("*");
                      return (
                        <>
                          {hasStar && (
                            <span className="text-[16px] font-semibold align-middle">
                              *
                            </span>
                          )}
                          {hasStar ? note.slice(1) : note}
                        </>
                      );
                    })()}
                  </div>
                }
                position="top"
                width={323}
              >
                <button className="flex items-center gap-1.5 text-white hover:text-white/80 transition-all font-sans">
                  <Text
                    as="span"
                    translationKey="staticTranslations.loadingScreen.disclaimer"
                    fallback="Disclaimer"
                    className="text-[14px]"
                  />
                  <div className="w-4 h-4 flex items-center justify-center">
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="8"
                        cy="8"
                        r="7.25"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                      <path
                        d="M8 11V11.01M8 8.5C8 7.5 9.5 7.5 9.5 6C9.5 5.17157 8.82843 4.5 8 4.5C7.17157 4.5 6.5 5.17157 6.5 6"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </Tooltip>
            )}

            <button
              onClick={() => {
                trackEvent(AnalyticsIds.termsInteract, {
                  state: true,
                  primaryTypeName: 'tnc',
                });
                setShowTerms(true);
              }}
              className="flex items-center gap-1.5 text-white hover:text-white/80 transition-all font-sans"
            >
              <Text
                as="span"
                translationKey="staticTranslations.loadingScreen.termsConditions"
                fallback="Terms & Conditions"
                className="text-[14px]"
              />
              <div className="w-4 h-4 flex items-center justify-center">
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7.25"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M8 11V11.01M8 8.5C8 7.5 9.5 7.5 9.5 6C9.5 5.17157 8.82843 4.5 8 4.5C7.17157 4.5 6.5 5.17157 6.5 6"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Sits between .videocontainer (z-index 1) and .car-image (z-index 2):
          at z-index 2 it clears the video/overlay, and because .car-image is
          the later sibling at the same z-index the sliding image passes over
          the top of these pointers rather than under them. Keep it declared
          before .car-image - swapping the order swaps which one wins. */}
      <PerformanceStats />

      <div
        className={`car-image ${isRTL ? "car-image--rtl" : ""}`}
        style={{
          backgroundImage: `url("${assetUrl}${loadingImage}")`,
          clipPath: isRTL
            ? "polygon(0% 0%, 86% 0%, 100% 100%, 0% 100%)" // RTL: mirror of LTR — slant cuts top-right corner
            : "polygon(14% 0, 100% 0%, 100% 100%, 0% 100%)", // LTR: narrow at top-left, wide at bottom
        }}
      >
        <div
          className="diagonal-line"
          style={{
            left: isRTL ? "auto" : "-3%",
            right: isRTL ? "-3%" : "auto",
            clipPath: isRTL
              ? "polygon(0% 0%, 100% 0%, 100% 100%, 80% 100%)" // RTL: mirror of LTR diagonal-line
              : undefined,
          }}
        ></div>
      </div>

      {/* Progress Bar for Main Screen */}
      {/* {!isLoadingComplete && (
        <div className="fixed bottom-[20%] left-1/2 -translate-x-1/2 w-[300px] md:w-[400px] z-50 flex flex-col items-center gap-2 pointer-events-none">
          <span className="text-white font-bold text-lg drop-shadow-md">
            {progress}%
          </span>
          <ProgressBar progress={progress} />
          <p className="text-white/80 text-sm drop-shadow-md">
            {t('staticTranslations.common.loading', 'Loading experience...')}
          </p>
        </div>
      )} */}

      {/* Presentation-mode journey picker (Stingray / GS X) — bottom banner. */}
      <JourneySelector />

      {/* Mobile Slider Button with black background */}
      {isMobile && (
        <div className="mobile-slider-wrapper">
          <SliderButton
            isLoadingComplete={buttonEnabled}
            handleEnterClick={handleEnterClick}
          />
        </div>
      )}

      {/* Desktop Button - 1.0 Style Animation with 2.0 Content */}
      {!isMobile && (
        <div
          className={`enter-cta-container ${
            isRTL ? "bottom-left" : "bottom-right"
          } ${hasJourneySelector ? "on-journey-selector" : ""}`}
        >
          <button
            onClick={handleEnterClick}
            disabled={!buttonEnabled}
            className="enter-cta"
            style={{ flexDirection: isRTL ? "row" : "row" }}
          >
            {/* Main section with 1.0 style background animations */}
            <div
              className="enter-cta-main"
              style={{
                clipPath: isRTL
                  ? "polygon(0% 0%, 92% 0%, 100% 100%, 8% 100%)"
                  : "polygon(8% 0, 100% 0, 92% 100%, 0% 100%)",
                padding: isRTL ? "0 32px 0 24px" : "0 24px 0 32px",
              }}
            >
              {!buttonEnabled ? (
                <>
                  <div className="enter-cta-loading-bg loading-active" />
                  <Text
                    as="span"
                    translationKey="staticTranslations.loadingScreen.loading"
                    fallback="Loading"
                    className="enter-cta-loading-text font-sans"
                  />
                </>
              ) : (
                <>
                  <div className="enter-cta-loaded-shimmer" />
                  <Text
                    as="span"
                    translationKey="staticTranslations.loadingScreen.enterConfigurator"
                    fallback="Enter Configurator"
                    className="enter-cta-text font-sans"
                  />
                </>
              )}
            </div>
            {/* Blue arrow section */}
            <div
              className="enter-cta-arrow"
              style={{
                marginLeft: isRTL ? "0" : "-16px",
                marginRight: isRTL ? "-16px" : "0",
                clipPath: isRTL
                  ? "polygon(0% 0%, 80% 0%, 100% 100%, 0% 100%)"
                  : "polygon(20% 0, 100% 0, 100% 100%, 0% 100%)",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transform: isRTL ? "rotate(180deg)" : "none" }}
              >
                {/* currentColor, not white: the arrow block's fill is brand-
                    tokened, so on a light fill (Cadillac uses white) a
                    hardcoded white chevron would be invisible. */}
                <path
                  d="M1 1L7 7L1 13"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
      )}

      {/* Modals (TermsModal, SpecDetails, SaveDialog, ShareModal) not available in standalone configurator */}
      <TermsModal
        visible={showTerms}
        onClose={() => {
          trackEvent(AnalyticsIds.termsInteract, {
            state: false,
            primaryTypeName: 'tnc',
          });
          setShowTerms(false);
        }}
      />
      <SpecDetails
        isOpen={isSpecDetailsOpen}
        onClose={() => {
          trackEvent(AnalyticsIds.specDetailsInteract, {
            state: false,
            primaryTypeName: 'specDetails',
          });
          setIsSpecDetailsOpen(false);
        }}
        onBookingClick={() => {
          trackEvent(AnalyticsIds.specSaveInteract, {
            parent: 'specDetails',
            primaryTypeName: 'save',
          });
          setIsSpecDetailsOpen(false);
          onSave?.();
        }}
        onShareClick={() => { setIsSpecDetailsOpen(false); onShare?.(); }}
      />
    </div>
  );
};

export default LoadingProgress;
